/* ============================================================================
   Furniva — the add-to-cart moment.

   One module owns what happens when anything on the site is added to the cart,
   so the product page, the card quick-add, the real-homes frames, the room
   photos and the wishlist all behave identically. Previously each call site
   wrote its own: some turned the button green, some opened the drawer, and the
   header count only caught up on a later event.

   The sequence, which is the one the interaction-design literature settles on
   — press acknowledged, work shown, result confirmed, destination pointed at:

     1. press    the control takes the press (scale down, ~120ms)
     2. working  the label slides out, a spinner takes its place
     3. flight   the product photo lifts off the page and arcs into the cart
                 icon, shrinking as it goes (~620ms)
     4. landing  the cart icon takes the impact and its count rolls up by the
                 number actually added
     5. confirm  a tick draws itself on the button, a light sweeps across it,
                 and after a beat the original label returns

   The cart drawer is deliberately NOT opened. Interrupting someone who is still
   looking at a product to show them a panel they did not ask for is the thing
   this replaces; the header count is the receipt, and it is the thing that
   moves.

   Everything here degrades: with prefers-reduced-motion the flight is skipped
   and the states simply swap, and if any of it throws, the add has already
   happened on the server and the page is still correct.
   ============================================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ---- the cart icon we are aiming at ------------------------------------
     Whichever is actually on screen: the header bag on a desktop, the bottom
     navigation's cart on a phone. A hidden one is not a target — an animation
     that flies to a point nobody can see is worse than none.
     ---------------------------------------------------------------------- */
  function cartTarget() {
    var candidates = $$('[data-fv-cart-count]').concat($$('[data-fv-cart-open]'));
    for (var i = 0; i < candidates.length; i++) {
      var node = candidates[i];
      var box = node.getBoundingClientRect();
      if (box.width > 0 && box.height > 0 && box.top < window.innerHeight && box.bottom > 0) {
        return node.closest('a, button') || node;
      }
    }
    return null;
  }

  /* ---- the photo to send ---------------------------------------------------
     Found from the control outwards: the card, the frame, the gallery. Falls
     back to nothing, in which case the flight is skipped rather than faked.
     ---------------------------------------------------------------------- */
  function visible(image) {
    if (!image) return null;
    var box = image.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return null;
    // A photo scrolled off the screen is not worth flying; the count still moves.
    if (box.bottom < 0 || box.top > window.innerHeight) return null;
    return image;
  }

  /* The biggest photograph actually on screen inside a given box. Naming a
     selector per card layout does not survive contact with this theme — a
     product card hides its desktop image and shows a rail instead when it has
     more than one shot, a reel frame is different again — but "the largest
     visible image in here" is true of all of them. */
  function largestIn(scope) {
    var best = null;
    var bestArea = 0;
    $$('img', scope).forEach(function (image) {
      if (!visible(image)) return;
      var box = image.getBoundingClientRect();
      var area = box.width * box.height;
      // Ignore anything badge-sized; it is an icon, not the piece.
      if (area < 2500 || area <= bestArea) return;
      bestArea = area;
      best = image;
    });
    return best;
  }

  function sourceImage(origin) {
    if (!origin) return null;
    // Nearest enclosing piece of product furniture first — a card, a reel
    // frame, a room photo — so a grid of eight cards sends the right one.
    var scope = origin.closest('.fv-card, .fv-reel, .fv-homecard, .fv-wish__item');
    if (scope) {
      var local = largestIn(scope);
      if (local) return local;
    }
    // Otherwise this is a product page, where the control sits in a form with
    // no photograph in it and the subject is the gallery: the shot on screen,
    // whichever of the two layouts is showing.
    return (
      visible($('.fv-gal__main')) ||
      visible($('.fv-gal__hero img')) ||
      visible($('.fv-gal__slide img')) ||
      largestIn($('.fv-pdp__main') || document) ||
      null
    );
  }

  /* ---- flight ------------------------------------------------------------
     A clone, not the photo itself: moving the real element would collapse the
     layout underneath it. Fixed-position, so it is unaffected by any scrolling
     that happens mid-flight, and pointer-events none so it never swallows a
     click. The arc comes from animating a wrapper's translate while the inner
     element scales — one horizontal easing, one vertical, which is what gives
     it the thrown-object feel rather than a straight diagonal slide.
     ---------------------------------------------------------------------- */
  function fly(origin, onLanded) {
    var done = typeof onLanded === 'function' ? onLanded : function () {};
    if (reduceMotion.matches || typeof Element.prototype.animate !== 'function') {
      done();
      return;
    }

    var image = sourceImage(origin);
    var target = cartTarget();
    if (!image || !target) { done(); return; }

    var from = image.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    if (!to.width || !to.height) { done(); return; }

    var ghost = image.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.className = 'fv-flyer__img';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.removeAttribute('loading');

    var shell = document.createElement('span');
    shell.className = 'fv-flyer';
    shell.setAttribute('aria-hidden', 'true');
    shell.style.left = from.left + 'px';
    shell.style.top = from.top + 'px';
    shell.style.width = from.width + 'px';
    shell.style.height = from.height + 'px';
    shell.appendChild(ghost);
    document.body.appendChild(shell);

    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    var duration = 620;

    // Horizontal travel eases out; vertical eases in. Apart, they read as an arc.
    var across = shell.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + dx + 'px)' }],
      { duration: duration, easing: 'cubic-bezier(.32,.12,.30,1)', fill: 'forwards' }
    );
    shell.animate(
      [{ marginTop: '0px' }, { marginTop: dy + 'px' }],
      { duration: duration, easing: 'cubic-bezier(.62,.02,.84,.56)', fill: 'forwards' }
    );
    ghost.animate(
      [
        { transform: 'scale(1)', opacity: 1, borderRadius: '10px' },
        { transform: 'scale(.52)', opacity: 1, offset: 0.55 },
        { transform: 'scale(.16)', opacity: 0.25, borderRadius: '50%' }
      ],
      { duration: duration, easing: 'cubic-bezier(.4,0,.5,1)', fill: 'forwards' }
    );

    var landed = false;
    function land() {
      if (landed) return;
      landed = true;
      if (shell.parentNode) shell.parentNode.removeChild(shell);
      impact(target);
      done();
    }
    across.addEventListener('finish', land);
    // The animation can be dropped entirely on a background tab.
    window.setTimeout(land, duration + 120);
  }

  /* The cart icon takes the hit: a short squash-and-recover, so the count that
     changes a moment later has something that drew the eye to it first. */
  function impact(target) {
    if (!target || reduceMotion.matches || typeof target.animate !== 'function') return;
    target.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(.86)', offset: 0.35 },
        { transform: 'scale(1.16)', offset: 0.65 },
        { transform: 'scale(1)' }
      ],
      { duration: 460, easing: 'cubic-bezier(.34,1.56,.64,1)' }
    );
  }

  /* ---- the count ---------------------------------------------------------
     Set from the cart's own reply rather than incremented locally, so two adds
     in flight at once cannot leave the badge telling a different story from the
     cart. Every badge on the page is kept in step — the header bag and the
     bottom navigation are both [data-fv-cart-count].
     ---------------------------------------------------------------------- */
  function setCount(total) {
    if (total == null || isNaN(total)) return;
    $$('[data-fv-cart-count]').forEach(function (node) {
      var before = parseInt(node.textContent, 10);
      node.textContent = total;
      if (before === total) return;
      node.classList.remove('is-bumped');
      // Reflow, or re-adding the class in the same frame does nothing.
      void node.offsetWidth;
      node.classList.add('is-bumped');
      window.setTimeout(function () { node.classList.remove('is-bumped'); }, 520);
    });
  }

  /* ---- the control -------------------------------------------------------
     The button keeps its own width throughout every state. A button that
     resizes as it goes from "Add to cart" to "Adding…" to "Added" shunts
     whatever sits beside it, which on a card grid is the whole row.
     ---------------------------------------------------------------------- */
  function lockWidth(button) {
    if (!button || button.dataset.fvFxWidth) return;
    var width = button.getBoundingClientRect().width;
    if (width > 0) {
      button.dataset.fvFxWidth = String(width);
      button.style.minWidth = Math.ceil(width) + 'px';
    }
  }

  function working(button) {
    if (!button) return;
    lockWidth(button);
    if (!button.dataset.fvFxLabel) button.dataset.fvFxLabel = button.innerHTML;
    button.disabled = true;
    button.classList.remove('is-added', 'is-failed');
    button.classList.add('is-working');
    button.innerHTML =
      '<span class="fv-fx__spin" aria-hidden="true"></span>' +
      '<span class="fv-vh">Adding to your cart</span>';
  }

  function restore(button) {
    if (!button || !button.dataset.fvFxLabel) return;
    button.classList.remove('is-working', 'is-added', 'is-failed');
    button.innerHTML = button.dataset.fvFxLabel;
    button.disabled = false;
    delete button.dataset.fvFxLabel;
  }

  /* The tick is drawn, not faded in: the stroke runs on from the press, which
     is what makes it read as a consequence of it rather than a new thing that
     appeared. The sweep behind it is a single pass of light and is purely
     decoration, so it is dropped under reduced motion along with the drawing. */
  function confirm(button, label) {
    if (!button) return;
    button.classList.remove('is-working');
    button.classList.add('is-added');
    button.innerHTML =
      '<span class="fv-fx__done">' +
      '<svg class="fv-fx__tick" viewBox="0 0 24 24" width="17" height="17" fill="none" ' +
      'stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" ' +
      'aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>' +
      '<span>' + (label || 'Added') + '</span></span>';
    if ('vibrate' in navigator) navigator.vibrate(18);
    window.setTimeout(function () { restore(button); }, 1900);
  }

  function failed(button, message) {
    if (!button) return;
    button.classList.remove('is-working');
    button.classList.add('is-failed');
    button.innerHTML = '<span>' + (message || 'Try again') + '</span>';
    button.disabled = false;
    window.setTimeout(function () { restore(button); }, 2600);
  }

  /* ---- the whole moment, in one call -------------------------------------
     Call `working` before the request, then this once it has come back.

     The count is read from /cart.js rather than taken from the add response:
     /cart/add.js answers with the line item that was added, not the cart, and
     carries no total at all for a form POST — trusting it left the badge
     showing whatever it had before. Reading the cart is also the only thing
     that stays right when two adds are in flight at once.

     The read is started immediately and applied when the photo lands, so the
     number changes at the moment something arrives at the icon rather than a
     beat before it.
     ---------------------------------------------------------------------- */
  function added(button, cartReply, label) {
    var counted = countFromCart();
    fly(button, function () {
      counted.then(function (total) { setCount(total); }).catch(function () {});
    });
    confirm(button, label);
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function countFromCart() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) { return cart.item_count; });
  }

  // Ask the cart what it holds and put it on the badge straight away.
  function refreshCount() {
    countFromCart()
      .then(function (total) { setCount(total); })
      .catch(function () { /* the badge simply stays as it was */ });
  }

  // Horizon and the cart page both announce their own updates.
  document.addEventListener('cart:update', function (event) {
    var count = event && event.detail && event.detail.data ? event.detail.data.itemCount : null;
    if (count != null) setCount(count);
  });

  window.FurnivaAddFx = {
    working: working,
    added: added,
    failed: failed,
    restore: restore,
    setCount: setCount,
    refreshCount: refreshCount,
    fly: fly
  };
})();
